import React, { useEffect, useState } from "react";
import { deleteInquiry, getInquiries, updateInquiry } from "../services/api";
import { confirmAppAction, showAppToast } from "../utils/feedback";
import { formatDate } from "../utils/helpers";
import "./InquiriesPage.css";

const formatDateTime = (value) =>
  `${formatDate(value)} ${new Date(value).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit"
  })}`;

function InquiriesPage() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workingId, setWorkingId] = useState("");

  const loadInquiries = async () => {
    try {
      setLoading(true);
      const response = await getInquiries();
      setInquiries(response.data);
    } catch (error) {
      showAppToast({
        type: "error",
        title: "Failed to load inquiries",
        message: error.response?.data?.error || "The inquiry list could not be loaded."
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInquiries();
  }, []);

  const handleMarkReviewed = async (inquiryId) => {
    try {
      setWorkingId(inquiryId);
      const response = await updateInquiry(inquiryId, { status: "reviewed" });
      setInquiries((current) =>
        current.map((item) => (item._id === inquiryId ? response.data.inquiry : item))
      );
      showAppToast({
        type: "success",
        title: "Inquiry updated",
        message: "The inquiry is now marked as reviewed."
      });
    } catch (error) {
      showAppToast({
        type: "error",
        title: "Failed to update inquiry",
        message: error.response?.data?.error || "The inquiry status could not be updated."
      });
    } finally {
      setWorkingId("");
    }
  };

  const handleDelete = async (inquiry) => {
    const confirmed = await confirmAppAction({
      title: "Delete inquiry",
      message: `This will permanently remove the inquiry from ${inquiry.name}.`,
      confirmLabel: "Delete",
      type: "danger"
    });

    if (!confirmed) {
      return;
    }

    try {
      setWorkingId(inquiry._id);
      await deleteInquiry(inquiry._id);
      setInquiries((current) => current.filter((item) => item._id !== inquiry._id));
      showAppToast({
        type: "success",
        title: "Inquiry deleted",
        message: "The inquiry has been removed from the list."
      });
    } catch (error) {
      showAppToast({
        type: "error",
        title: "Failed to delete inquiry",
        message: error.response?.data?.error || "The inquiry could not be removed."
      });
    } finally {
      setWorkingId("");
    }
  };

  const totalInquiries = inquiries.length;
  const newInquiries = inquiries.filter((item) => item.status === "new").length;
  const reviewedInquiries = inquiries.filter((item) => item.status === "reviewed").length;

  return (
    <section className="inquiries-page">
      <div className="inquiries-page__header">
        <div>
          <span className="inquiries-page__eyebrow">Inquiry Desk</span>
          <h1>Inquiries</h1>
          <p>Track every contact submission received from the public website.</p>
        </div>
        <button className="inquiries-page__refresh" onClick={loadInquiries} disabled={loading}>
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      <div className="inquiries-page__stats">
        <article className="inquiries-page__stat-card">
          <span>Total Inquiries</span>
          <strong>{totalInquiries}</strong>
        </article>
        <article className="inquiries-page__stat-card">
          <span>New</span>
          <strong>{newInquiries}</strong>
        </article>
        <article className="inquiries-page__stat-card">
          <span>Reviewed</span>
          <strong>{reviewedInquiries}</strong>
        </article>
      </div>

      <div className="inquiries-page__list">
        {loading ? (
          <div className="inquiries-page__empty">Loading inquiries...</div>
        ) : inquiries.length === 0 ? (
          <div className="inquiries-page__empty">No inquiries have been submitted yet.</div>
        ) : (
          <div className="inquiries-page__table">
            <div className="inquiries-page__table-head">
              <span>Name</span>
              <span>Contact</span>
              <span>Details</span>
              <span>Received</span>
              <span>Status</span>
              <span>Action</span>
            </div>

            {inquiries.map((inquiry) => (
              <article key={inquiry._id} className="inquiries-page__row">
                <div className="inquiries-page__cell inquiries-page__cell--name">
                  <strong>{inquiry.name}</strong>
                </div>

                <div className="inquiries-page__cell inquiries-page__cell--contact">
                  <div className="inquiries-page__contact-lines">
                    <a href={`mailto:${inquiry.email}`}>{inquiry.email}</a>
                    <a href={`tel:${inquiry.mobile || ""}`}>{inquiry.mobile || "No mobile number"}</a>
                  </div>
                </div>

                <div className="inquiries-page__cell inquiries-page__cell--details">
                  <p className="inquiries-page__details">{inquiry.details}</p>
                </div>

                <div className="inquiries-page__cell inquiries-page__cell--meta">
                  <span>{formatDateTime(inquiry.createdAt)}</span>
                  {inquiry.updatedAt && inquiry.updatedAt !== inquiry.createdAt && (
                    <small>Updated: {formatDateTime(inquiry.updatedAt)}</small>
                  )}
                </div>

                <div className="inquiries-page__cell inquiries-page__cell--status">
                  <span
                    className={`inquiries-page__status inquiries-page__status--${inquiry.status}`}
                  >
                    {inquiry.status}
                  </span>
                </div>

                <div className="inquiries-page__cell inquiries-page__cell--actions">
                  <div className="inquiries-page__actions">
                    <button
                      className="inquiries-page__action inquiries-page__action--primary"
                      onClick={() => handleMarkReviewed(inquiry._id)}
                      disabled={workingId === inquiry._id || inquiry.status === "reviewed"}
                    >
                      {inquiry.status === "reviewed" ? "Reviewed" : "Mark Reviewed"}
                    </button>
                    <button
                      className="inquiries-page__action inquiries-page__action--danger"
                      onClick={() => handleDelete(inquiry)}
                      disabled={workingId === inquiry._id}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default InquiriesPage;
